// Chart history: where older bars come from, and how they map onto the live series.
//
// GET /api/Chart/{symbol}_{tf} serves the Orleans BarsGrain, which only holds what
// has been built *since the symbol was subscribed*. Open a chart on a symbol you
// just clicked and you get one in-progress candle. Everything older comes from
// TimescaleDB.
//
// ── Which timeframes are stored, and which are derived ───────────────────────
//
// Only 1-minute and daily bars are persisted:
//   Minute/1  — written per tick-built bar by OrleansTimescaleDbIntegrationService
//               (and backfilled nightly by MinuteBarBackfillService)
//   Daily/1   — written by HistoricalBarService, which is session/split aware
//
// Everything else is a deterministic rollup, so we aggregate on read via
// /aggregated (TimescaleDB time_bucket) instead of persisting eight timeframes
// per symbol. Storing them would mean a bar builder per timeframe per symbol —
// the exact fan-out SubscribeWithTimeframe was introduced to remove, and a
// multiplier on the ingestion path that has OOM-killed the API before.
//
// Weekly derives from Daily rather than Minute: daily bars already carry the
// session and split handling, and it is three orders of magnitude less to scan.

import { API_BASE } from '@/env';
import { apiFetch } from '@/auth/apiClient';
import type { BarData } from '@/types';

/** Below this many live bars, go get history. A healthy chart has ~100. */
export const HISTORY_TOP_UP_BELOW = 30;

/** Server shape from both /timescaledb and /aggregated (BarTimeseries). */
interface BarTimeseries {
  symbol: string;
  timestamp: string;
  period: string;
  timeUnits: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export interface Timeframe { period: string; timeUnits: number; }

/**
 * "1M" → Minute/1, "4H" → Hourly/4, "1D" → Daily/1, "1W" → Weekly/1.
 *
 * The backend's own parser (Screener.ParseTimeframe) only accepts [MHDS] and has
 * no case for weeks, so "1W" is handled here explicitly — the chart data path
 * needs it even though the subscribe path can't express it.
 */
export function parseTimeframe(tf: string): Timeframe | null {
  const t = tf.toUpperCase();
  if (/^(\d+)W$/.test(t)) return { period: 'Weekly', timeUnits: parseInt(t, 10) || 1 };
  const m = /^(\d+)([MHDS])$/.exec(t);
  if (!m) return null;
  const timeUnits = parseInt(m[1], 10);
  if (!Number.isFinite(timeUnits) || timeUnits <= 0) return null;
  const period = { M: 'Minute', H: 'Hourly', D: 'Daily', S: 'Second' }[m[2]];
  return period ? { period, timeUnits } : null;
}

/** Milliseconds one bar of this timeframe covers. */
export function barMs(tf: Timeframe): number {
  const unit = tf.period === 'Second' ? 1_000
    : tf.period === 'Minute' ? 60_000
    : tf.period === 'Hourly' ? 3_600_000
    : tf.period === 'Daily' ? 86_400_000
    : tf.period === 'Weekly' ? 604_800_000
    : 2_592_000_000; // Monthly, approximate — only used to size look-back windows
  return unit * tf.timeUnits;
}

/** True when this timeframe is persisted as-is rather than rolled up on read. */
function isStored(tf: Timeframe): boolean {
  return (tf.period === 'Minute' && tf.timeUnits === 1)
      || (tf.period === 'Daily' && tf.timeUnits === 1);
}

/** The stored timeframe a derived one rolls up from. */
function sourceFor(tf: Timeframe): Timeframe {
  return tf.period === 'Weekly' || tf.period === 'Monthly'
    ? { period: 'Daily', timeUnits: 1 }
    : { period: 'Minute', timeUnits: 1 };
}

const DAY_MS = 86_400_000;

/**
 * Look-back window wide enough to contain `wanted` bars of *trading*. The clock
 * keeps running overnight, at weekends and on holidays while bars do not, so a
 * span computed from bar length alone is far too short: 200 one-minute bars is
 * 20 hours of wall clock, and at Monday's open — or after a holiday — that
 * window contains no trading session at all and the top-up comes back empty.
 * Intraday timeframes therefore get a multiplier AND a floor of several days.
 */
export function lookbackMs(tf: Timeframe, wanted: number): number {
  const span = barMs(tf) * wanted;
  const intraday = tf.period === 'Minute' || tf.period === 'Hourly' || tf.period === 'Second';
  return intraday
    ? Math.max(span * 6, 6 * DAY_MS)
    : Math.max(span * 2, 30 * DAY_MS);
}

/**
 * Bars for an explicit window. Routes to the stored table or the time_bucket
 * rollup depending on the timeframe. Returns [] rather than throwing — history is
 * additive on top of the live series and must never take the chart down.
 */
export async function fetchBarWindow(
  symbol: string,
  timeframe: string,
  from: Date,
  to: Date,
): Promise<BarData[]> {
  const tf = parseTimeframe(timeframe);
  if (!tf) return [];

  const sym = encodeURIComponent(symbol);
  let url: string;

  if (isStored(tf)) {
    const qs = new URLSearchParams({
      period: tf.period,
      timeUnits: String(tf.timeUnits),
      from: from.toISOString(),
      to: to.toISOString(),
      source: 'stream',
    });
    url = `${API_BASE}/api/TimescaleDb/bars/${sym}/timescaledb?${qs}`;
  } else {
    const src = sourceFor(tf);
    const qs = new URLSearchParams({
      sourcePeriod: src.period,
      sourceTimeUnits: String(src.timeUnits),
      targetPeriod: tf.period,
      targetTimeUnits: String(tf.timeUnits),
      from: from.toISOString(),
      to: to.toISOString(),
    });
    url = `${API_BASE}/api/TimescaleDb/bars/${sym}/aggregated?${qs}`;
  }

  // Failures PROPAGATE. Collapsing them to [] made a transient 500, a token refresh, or a dropped
  // connection indistinguishable from "this symbol has no older bars" — loadOlderBars returned 0
  // and attachOlderBarLoader permanently marked the series exhausted, disabling pan-back until the
  // user changed symbol or timeframe. An empty array now means exactly one thing: the query
  // succeeded and there are no more rows.
  const res = await apiFetch(url);
  if (!res.ok) throw new Error(`bar history ${res.status} for ${sym} ${timeframe}`);
  const rows: BarTimeseries[] = await res.json();
  return Array.isArray(rows) ? rows.map(toBarData) : [];
}

/** The `wanted` bars ending at `anchor` (default: now) for this symbol/timeframe. */
export async function fetchBarHistory(
  symbol: string,
  timeframe: string,
  wanted = 200,
  anchor?: Date,
): Promise<BarData[]> {
  const tf = parseTimeframe(timeframe);
  if (!tf) return [];
  // Anchored to the newest bar the chart already holds rather than to the wall
  // clock. For a live chart those are the same instant, but a chart showing a
  // past window must not have *today's* candles spliced onto the end of it.
  const to = anchor ?? new Date();
  const from = new Date(to.getTime() - lookbackMs(tf, wanted));
  const rows = await fetchBarWindow(symbol, timeframe, from, to);
  return rows.slice(-wanted);
}

/**
 * The `wanted` bars immediately older than `before` — the pan-back page.
 * Returns [] when the window is genuinely empty, which the caller treats as
 * "no more history" and stops asking.
 */
export async function fetchOlderBars(
  symbol: string,
  timeframe: string,
  before: Date,
  wanted = 200,
): Promise<BarData[]> {
  const tf = parseTimeframe(timeframe);
  if (!tf) return [];
  const to = new Date(before.getTime() - 1);   // exclusive: don't refetch the edge bar
  const from = new Date(to.getTime() - lookbackMs(tf, wanted));
  const rows = await fetchBarWindow(symbol, timeframe, from, to);
  return rows.slice(-wanted);
}

// Persisted and aggregated bars are closed by definition. The bid/ask fields
// exist only on the live tick-built shape and nothing on the chart path reads
// them — the candlestick mapping uses barStartTime + open/high/low/close.
function toBarData(b: BarTimeseries): BarData {
  return {
    symbol: b.symbol,
    openBidPrice: 0, openAskPrice: 0,
    closeBidPrice: 0, closeAskPrice: 0,
    highBidPrice: 0, highAskPrice: 0,
    lowBidPrice: 0, lowAskPrice: 0,
    volume: b.volume,
    barStartTime: b.timestamp,
    barEndTime: b.timestamp,
    opened: true,
    closed: true,
    isFinal: true,
    open: b.open,
    high: b.high,
    low: b.low,
    close: b.close,
  } as BarData;
}

/**
 * History underneath, live on top. The live bars win on any timestamp both sides
 * carry — the grain's copy is the one still being updated by ticks, so the
 * persisted copy of the current bar is stale the moment it is written.
 */
export function mergeBars(history: BarData[], live: BarData[]): BarData[] {
  const byTime = new Map<number, BarData>();
  for (const b of history) byTime.set(new Date(b.barStartTime).getTime(), b);

  for (const b of live) {
    const key = new Date(b.barStartTime).getTime();
    const persisted = byTime.get(key);
    byTime.set(key, persisted ? combineBucket(persisted, b) : b);
  }

  return [...byTime.values()]
    .sort((a, b) => new Date(a.barStartTime).getTime() - new Date(b.barStartTime).getTime());
}

/**
 * Same bucket seen from both sides: a persisted aggregate built from every 1-minute bar in the
 * bucket so far, and a live bar holding only the ticks since this chart subscribed.
 *
 * Live used to win outright, which is wrong whenever the live bar is the thinner of the two — open
 * a 4H chart at 11:00 and its live 08:00 candle contains three minutes of ticks while the persisted
 * aggregate contains three hours. Take the persisted bar's shape and extend it with the live tail:
 * the open is the bucket's true open, the extremes span both, the close is the freshest tick.
 *
 * Volume takes the MAX rather than the sum — the two sources overlap in time, so adding them would
 * double-count the minutes present in both.
 */
function combineBucket(persisted: BarData, live: BarData): BarData {
  return {
    ...persisted,
    high: Math.max(persisted.high, live.high),
    low: Math.min(persisted.low, live.low),
    close: live.close,
    volume: Math.max(persisted.volume ?? 0, live.volume ?? 0),
  };
}
